/* eslint-disable react/display-name */
import React, {useMemo, useState} from "react";
import PropTypes from "prop-types";
import {Col, FormGroup} from "reactstrap";
import Select, {components} from "react-select";
import AsteriskIcon from "../../asteriskIcon";
import HelpIcon from "../../HelpIcon/HelpIcon";

/** This component is wrapper around Select component of react select. With few extra features
 * like Label is form field required and help text.
 */

const COLLAPSED_LIMIT = 2;

const OverflowBadge = ({count, isExpanded, onClick}) => (
  <button
    type="button"
    onClick={onClick}
    className={`select-overflow-badge${isExpanded ? " select-overflow-badge--expanded" : ""}`}
  >
    {isExpanded ? "show less" : `+${count} more`}
  </button>
);

const MultiValueContainer = ({children, getValue, ...props}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const values = getValue();

  if (!Array.isArray(children)) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  const [chips, input] = children;
  const isOverflowing = values.length > COLLAPSED_LIMIT;
  const chipsArray = chips ? [...(Array.isArray(chips) ? chips : [chips])] : [];
  const visibleChips =
    isExpanded || !isOverflowing ? chipsArray : chipsArray.slice(0, COLLAPSED_LIMIT);
  return (
    <components.ValueContainer {...props}>
      {visibleChips}
      {isOverflowing && (
        <OverflowBadge
          count={values.length - COLLAPSED_LIMIT}
          isExpanded={isExpanded}
          onClick={() => setIsExpanded((prev) => !prev)}
        />
      )}
      {input}
    </components.ValueContainer>
  );
};

const SelectCreator = React.forwardRef((props, ref) => {
  const {
    description,
    label,
    inSingleRow = false,
    value,
    options,
    onChange = () => {},
    large = false,
    colSize = "6",
    isRequired = false,
    help_text,
    isMulti = false,
    onMenuOpen = () => {},
    ...rest
  } = props;

  const selectedValue = useMemo(() => {
    if (isMulti) {
      return value?.length > 0
        ? options.filter((option) => value.includes(option.value))
        : [];
    }
    return value ? options.find((option) => option.value === value) : "";
  }, [isMulti, value, options]);

  const onChangeMethod = (event) => {
    onChange({
      target: {
        label: isMulti ? event.map((e) => e.label) : event?.label,
        value: isMulti ? event.map((e) => e.value) : event?.value,
        ...rest,
      },
    });
  };

  const customComponents = useMemo(
    () => (isMulti ? {ValueContainer: MultiValueContainer} : {}),
    [isMulti]
  );

  return (
    <Col md={large ? "12" : colSize}>
      <FormGroup row>
        <Col md={inSingleRow ? 3 : 12}>
          <div className="d-flex ">
            {label && (
              <label className="form-control-label roboto-bold" htmlFor="input-name">
                {label}
              </label>
            )}
            {isRequired && <AsteriskIcon />}
            {help_text && (
              <span style={{padding: "0px 5px"}}>
                <HelpIcon text={help_text} target={label} place="right" />
              </span>
            )}
          </div>
        </Col>
        <Col md={inSingleRow ? 9 : 12}>
          <Select
            hasValue={true}
            value={selectedValue}
            options={options}
            onChange={onChangeMethod}
            isMulti={isMulti}
            closeMenuOnSelect={!isMulti}
            onMenuOpen={onMenuOpen}
            ref={ref}
            components={customComponents}
            {...rest}
          />
          {description && (
            <div className="text-muted small" style={{marginTop: "2px"}}>
              {description}
            </div>
          )}
        </Col>
      </FormGroup>
    </Col>
  );
});

SelectCreator.propTypes = {
  /** Label for form field */
  label: PropTypes.string,
  /** Wether to show label and input field in single row */
  inSingleRow: PropTypes.bool,
  /** Value of selected option.
   * String for single select and Array for multi select
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  /** Options to be displayed in select dropdown */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  /** Callback function which will be executed on selecting or clearing option */
  onChange: PropTypes.func,
  /** If this prop is set to true then this component will take 100% width of parent component */
  large: PropTypes.bool,
  /** This prop will be passed as colSize to reactstrap Col component */
  colSize: PropTypes.string,
  /** This prop determines wether form field is required */
  isRequired: PropTypes.bool,
  /** If value is passed for this prop this will be shown as tooltip when hovered on help icon */
  help_text: PropTypes.string,
  /** This prop determines wether select component allows multi select */
  isMulti: PropTypes.bool,
  /**This prop determines when a particular select elemnt is opened */
  onMenuOpen: PropTypes.func,
};

export default SelectCreator;
